import React from "react";
import CodePill from "../../../../shared/CodePill/CodePill";
import { colorForPrefix, toHex7 } from "../../../../../utils/color";
import type { Trade } from "../../../../../models";
import type { CodeSource, CodeGroup } from "../codeGroups";
import "./MergeModal.css";

interface Props {
  groups: CodeGroup[];
  trades: Trade[];
  /** The trade the merged code defaults to — the one most of the selected codes
   *  already carry (so merging a traded code with an untraded one keeps it). */
  defaultTradeId: number | null;
  onMerge: (
    sources: CodeSource[],
    target: { code: string; name: string; color: string },
    tradeId: number | null,
  ) => void;
  onClose: () => void;
}

/** Names the unified code before a merge: every selected code's activities fold
 *  into this one code/name/color AND one trade. Defaults to the largest
 *  selected code + the trade most of them already share. */
const MergeModal: React.FC<Props> = ({
  groups,
  trades,
  defaultTradeId,
  onMerge,
  onClose,
}) => {
  const largest = groups.reduce(
    (a, b) => (b.activityCount > a.activityCount ? b : a),
    groups[0],
  );
  const [code, setCode] = React.useState(largest.code);
  const [name, setName] = React.useState(largest.name);
  const [color, setColor] = React.useState(
    toHex7(largest.color || colorForPrefix(largest.code)),
  );
  const [tradeId, setTradeId] = React.useState<number | null>(defaultTradeId);

  const total = groups.reduce((n, g) => n + g.activityCount, 0);
  const sources = groups.reduce<CodeSource[]>((acc, g) => acc.concat(g.sources), []);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const merge = () => {
    if (!code.trim()) return;
    onMerge(sources, { code: code.trim(), name: name.trim(), color }, tradeId);
    onClose();
  };

  return (
    <div className="takt-merge-overlay" onClick={onClose}>
      <div
        className="takt-merge-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Merge work codes"
      >
        <div className="takt-merge-head">Merge {groups.length} work codes</div>

        <div className="takt-merge-body">
          <div className="takt-merge-sub">
            {total} activit{total === 1 ? "y" : "ies"} will fold into one code:
          </div>
          <div className="takt-merge-sources">
            {groups.map((g) => (
              <div key={g.key} className="takt-merge-source">
                <CodePill
                  code={g.code || "—"}
                  color={g.color || colorForPrefix(g.code)}
                  className="takt-merge-source-pill"
                />
                <span className="takt-merge-source-name">{g.name || g.code}</span>
                <span className="takt-merge-source-count">{g.activityCount}</span>
              </div>
            ))}
          </div>

          <div className="takt-merge-target">
            <div className="takt-merge-target-label">Unified code</div>
            <div className="takt-merge-target-row">
              <input
                type="color"
                className="takt-merge-color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                aria-label="Unified color"
              />
              <input
                className="takt-merge-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="code"
                aria-label="Unified code value"
                autoFocus
              />
              <input
                className="takt-merge-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="name"
                aria-label="Unified code name"
              />
            </div>
          </div>

          <div className="takt-merge-target">
            <div className="takt-merge-target-label">Trade</div>
            <select
              className="takt-merge-trade"
              value={tradeId ?? ""}
              onChange={(e) => setTradeId(e.target.value === "" ? null : Number(e.target.value))}
              aria-label="Trade for the merged code"
            >
              <option value="">N/A — no trade</option>
              {trades.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="takt-merge-foot">
          <button type="button" className="takt-merge-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="takt-merge-go"
            onClick={merge}
            disabled={!code.trim()}
          >
            Merge into {code.trim() || "…"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MergeModal;
